import { Colors, Text } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import type { CompetencyHistoryQuestionResponse } from '@/types/performance-profile';

import { QuestionType } from '@/constants/QuestionType';
import { useReplaceDynamicTags } from '@/hooks/useReplaceDynamicTags';
import { getLocalizedText } from '@/utils/localization';

interface QuestionAnswerCardProps {
    question: CompetencyHistoryQuestionResponse;
    employeeFirstName?: string;
    dataTestId?: string;
}

export const QuestionAnswerCard = ({
    question,
    employeeFirstName,
    dataTestId,
}: QuestionAnswerCardProps) => {
    const { t } = useTranslation();
    const replaceDynamicTags = useReplaceDynamicTags();
    const isAnswered = !!question.answerId && !question.hasBeenSkipped;
    const selectedOptionIds = question.selectedOptionIds ?? [];
    const hasOptions = question.options.length > 0;

    return (
        <div
            className={'rounded-[12px] border border-purple-200 bg-purple-50/60 p-[12px]'}
            data-testid={dataTestId}
        >
            <div className={'flex items-start justify-between gap-2'}>
                <Text
                    weight={'semibold'}
                    size={'small'}
                    color={Colors.SECONDARY_700}
                >
                    {replaceDynamicTags({
                        text: getLocalizedText(question.label),
                        employee: employeeFirstName ? { firstName: employeeFirstName } : undefined,
                        competencyName: question.competencyName,
                        companyValueName: question.companyValueName,
                    })}
                </Text>
                {!isAnswered && (
                    <Text
                        size={'small'}
                        color={Colors.BASE_400}
                        className={'shrink-0 italic'}
                        data-testid={dataTestId ? `${dataTestId}-not-answered` : undefined}
                    >
                        {t(
                            'performanceProfile.skillsetAssessment.assessmentHistoryModal.notAnswered',
                        )}
                    </Text>
                )}
            </div>

            {hasOptions ? (
                <div className={'mt-3 flex flex-col gap-2'}>
                    {question.options.map((option) => {
                        const isSelected = selectedOptionIds.includes(option.id);
                        return isSelected ? (
                            <div
                                key={option.id}
                                className={
                                    'flex items-center justify-between gap-2 rounded-lg border border-purple-200 bg-purple-100 p-2'
                                }
                                data-testid={
                                    dataTestId
                                        ? `${dataTestId}-option-${option.id}-selected`
                                        : undefined
                                }
                            >
                                <div className={'flex items-center gap-2'}>
                                    <span
                                        className={
                                            'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2'
                                        }
                                        style={{ borderColor: Colors.SECONDARY_700 }}
                                    >
                                        <span
                                            className={'h-2 w-2 rounded-full'}
                                            style={{ backgroundColor: Colors.SECONDARY_700 }}
                                        />
                                    </span>
                                    <Text
                                        size={'small'}
                                        weight={'semibold'}
                                        color={Colors.SECONDARY_700}
                                    >
                                        {getLocalizedText(option.label)}
                                    </Text>
                                </div>
                            </div>
                        ) : (
                            <div
                                key={option.id}
                                className={'flex items-center gap-2 px-2 py-1'}
                                data-testid={
                                    dataTestId ? `${dataTestId}-option-${option.id}` : undefined
                                }
                            >
                                <span
                                    className={
                                        'h-4 w-4 shrink-0 rounded-full border-2 border-gray-300 bg-white'
                                    }
                                />
                                <Text
                                    size={'small'}
                                    color={Colors.BASE_400}
                                >
                                    {getLocalizedText(option.label)}
                                </Text>
                            </div>
                        );
                    })}
                    {question.questionType === QuestionType.RATING_SCALE &&
                        isAnswered &&
                        question.intValue !== undefined && (
                            <div
                                className={'mt-2 border-t border-gray-100 pt-2'}
                                data-testid={dataTestId ? `${dataTestId}-int-value` : undefined}
                            >
                                <Text
                                    size={'small'}
                                    weight={'semibold'}
                                    color={Colors.SECONDARY_700}
                                >
                                    {t(
                                        'performanceProfile.skillsetAssessment.assessmentHistoryModal.answer',
                                    )}
                                    : {question.intValue}
                                </Text>
                            </div>
                        )}
                </div>
            ) : (
                isAnswered && (
                    <Text
                        size={'small'}
                        color={Colors.BASE_500}
                        className={'mt-2'}
                    >
                        {question.textValue ??
                            (question.boolValue !== undefined
                                ? question.boolValue
                                    ? t('common.yes')
                                    : t('common.no')
                                : question.intValue?.toString()) ??
                            question.comment}
                    </Text>
                )
            )}
        </div>
    );
};
